import { useQuery } from '@tanstack/react-query';
import React, { useEffect } from 'react';
import { getSingleLiveTaskUpdates } from '../services/api/implementation/impl';
import pdfIcon from '../../shared/assets/svg/pdfIcon.svg';
import noDataIcon from '../../shared/assets/png/noDataFound.png';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';

const SingleTaskUpdatePage = ({ taskID }) => {
  const userId = Cookies.get('userId');
  const {
    data: TaskUpdates,
    refetch: refetchLiveTaskUpdates,
    isFetching
  } = useQuery({
    queryKey: ['getLiveTaskUpdates', taskID],
    queryFn: () => {
      if (!taskID) {
        return Promise.resolve([]); // protect against undefined values
      }
      return getSingleLiveTaskUpdates(taskID);
    },
    enabled: !!taskID
  });

  
  // Track page view
  useEffect(() => {
    mixpanel.track('Single Task Update Page Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    refetchLiveTaskUpdates();
  }, []);

  const handleFileAction = (url, name) => {
    if (!url) return;

    if (url.endsWith('.pdf')) {
      // If PDF, open in new tab
      window.open(url, '_blank');
    } else {
      // If Image, download directly
      const link = document.createElement('a');
      link.href = url;
      link.download = name || 'downloaded-image';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  if (isFetching) {
    return (
      <div className="flex justify-center items-center h-64 text-gray-600">Loading updates...</div>
    );
  }

  const updates = TaskUpdates?.data || [];

  if (!updates || updates.length === 0) {
    return (
      <div className="flex justify-center items-center h-[30rem] text-gray-600">
        <img src={noDataIcon} alt="No Data" className="w-[20rem] h-[15rem] object-cover" />
      </div>
    );
  }

  // Separate pinned and unpinned updates
  const pinnedUpdates = updates.filter(
    (update) => update?.user?.serviceProvider?.category_id === 2
  );
  const otherUpdates = updates.filter((update) => update?.user?.serviceProvider?.category_id !== 2);

  const sortedUpdates = [...pinnedUpdates, ...otherUpdates];

  return (
    <div className="p-4 space-y-6">
      {sortedUpdates.map((update) => {
        const document = update?.updateTaskimages?.[0];
        const documentUrl = document?.url;
        const documentName = document?.name;

        return (
          <div
            key={update?.id}
            className="border rounded-lg shadow-sm p-4 flex gap-4 items-start cursor-pointer hover:shadow-md transition relative"
            onClick={() => handleFileAction(documentUrl, documentName)}>
            {/* If it's pinned, show a pin icon */}
            {update?.user?.serviceProvider?.category_id === 2 && (
              <div className="absolute top-2 right-2">📌</div>
            )}
            <div className="flex flex-col justify-center items-start">
              <div className="flex-1">
                <h3 className="font-semibold text-gray-800">{update?.task?.name}</h3>
                <p className="text-gray-600 text-sm">Updated By: {update?.user?.name || '--'}</p>
                <p className="text-gray-600 text-sm">Description: {update?.description || '---'}</p>
                <p className="text-gray-600 text-sm">
                  Materials Used:{' '}
                  {update?.constructionUpdateMaterialMapping
                    ?.map(
                      (item) => `${item.item?.name || 'Unknown'}  (${item.quantity}${item.unit})`
                    )
                    .join(', ') || 'N/A'}
                </p>
              </div>

              {/* Document Thumbnail */}
              <div className="flex flex-wrap gap-2 mt-2">
                {update?.updateTaskimages?.map((doc, idx) => {
                  const isPdf = doc?.url?.endsWith('.pdf');
                  return (
                    <div
                      key={idx}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFileAction(doc.url, doc.name);
                      }}
                      className="w-16 h-16 cursor-pointer bg-gray-100 rounded-md overflow-hidden flex justify-center items-center border hover:shadow"
                      title={doc.name}>
                      {isPdf ? (
                        <img src={pdfIcon} alt="PDF" className="w-8 h-8" />
                      ) : (
                        <img
                          src={doc.url}
                          alt={doc.name || 'Document'}
                          className="object-cover w-full h-full"
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default SingleTaskUpdatePage;
